import PropTypes from 'prop-types';
// @mui
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemButton from '@mui/material/ListItemButton';
import LinearProgress from '@mui/material/LinearProgress';
// utils
import { fToNow } from 'src/utils/format-time';
// components
import Label from 'src/components/label';
import Iconify from 'src/components/iconify';

// ----------------------------------------------------------------------

export default function NotificationItem({ notification, onRead }) {
  const isUnRead = !notification.is_read && notification.isUnRead !== false;
  const task = notification.background_task;

  const errorCount =
    notification.meta_data?.error_count ??
    notification.background_task?.result_summary?.error_count ??
    (Array.isArray(notification.meta_data?.errors) ? notification.meta_data.errors.length : 0);

  const getIconInfo = () => {
    if (errorCount > 0 && notification.notification_type === 'TASK_COMPLETED') {
      return { icon: 'eva:alert-triangle-fill', color: 'warning.main', bgcolor: 'warning.lighter' };
    }
    switch (notification.notification_type) {
      case 'SUCCESS':
      case 'TASK_COMPLETED':
        return { icon: 'eva:checkmark-circle-2-fill', color: 'success.main', bgcolor: 'success.lighter' };
      case 'ERROR':
      case 'TASK_FAILED':
        return { icon: 'eva:alert-circle-fill', color: 'error.main', bgcolor: 'error.lighter' };
      case 'WARNING':
        return { icon: 'eva:alert-triangle-fill', color: 'warning.main', bgcolor: 'warning.lighter' };
      case 'TASK_PROGRESS':
        return { icon: 'solar:refresh-circle-bold', color: 'info.main', bgcolor: 'info.lighter' };
      default:
        return { icon: 'solar:bell-bing-bold-duotone', color: 'primary.main', bgcolor: 'primary.lighter' };
    }
  };

  const iconInfo = getIconInfo();

  const renderAvatar = (
    <ListItemAvatar>
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          bgcolor: iconInfo.bgcolor,
          color: iconInfo.color,
        }}
      >
        <Iconify icon={iconInfo.icon} width={24} />
      </Stack>
    </ListItemAvatar>
  );

  const formatModuleName = (mod) => {
    if (!mod || mod === 'general') return '';
    return mod.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const renderText = (
    <ListItemText
      disableTypography
      primary={
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          {notification.title}
        </Typography>
      }
      secondary={
        <Stack spacing={0.5}>
          {notification.message && (
            <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'pre-line' }}>
              {notification.message}
            </Typography>
          )}

          {task && (
            <Box sx={{ mt: 1, p: 1, borderRadius: 1, bgcolor: 'background.neutral' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  Progress: {task.progress_percent}%
                </Typography>
                {task.progress_total > 0 && (
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {task.progress_current} / {task.progress_total}
                  </Typography>
                )}
              </Stack>
              <LinearProgress
                variant="determinate"
                value={task.progress_percent || 0}
                color={task.status === 'FAILED' ? 'error' : 'primary'}
                sx={{ height: 6, borderRadius: 1 }}
              />
              {task.status_message && (
                <Typography variant="caption" sx={{ color: 'text.disabled', mt: 0.5, display: 'block' }} noWrap>
                  {task.status_message}
                </Typography>
              )}
            </Box>
          )}

          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ typography: 'caption', color: 'text.disabled', mt: 0.5 }}
          >
            <span>{fToNow(notification.created_at || notification.createdAt || new Date())}</span>
            {notification.module && notification.module !== 'general' && (
              <Label variant="soft" color="info" sx={{ height: 20, fontSize: 10 }}>
                {formatModuleName(notification.module)}
              </Label>
            )}
            {errorCount > 0 && (
              <Label variant="soft" color="error" sx={{ height: 20, fontSize: 10 }}>
                {errorCount} {errorCount === 1 ? 'error' : 'errors'}
              </Label>
            )}
          </Stack>
        </Stack>
      }
    />
  );

  const renderUnReadBadge = isUnRead && (
    <Box
      sx={{
        top: 26,
        width: 8,
        height: 8,
        right: 20,
        borderRadius: '50%',
        bgcolor: 'info.main',
        position: 'absolute',
      }}
    />
  );

  return (
    <ListItemButton
      disableRipple
      onClick={() => onRead?.(notification)}
      sx={{
        p: 2,
        alignItems: 'flex-start',
        borderBottom: (theme) => `dashed 1px ${theme.palette.divider}`,
        bgcolor: isUnRead ? 'action.hover' : 'transparent',
        '&:hover': {
          bgcolor: 'action.selected',
        },
      }}
    >
      {renderUnReadBadge}
      {renderAvatar}
      <Stack sx={{ flexGrow: 1, minWidth: 0, ml: 1.5 }}>
        {renderText}
      </Stack>
    </ListItemButton>
  );
}

NotificationItem.propTypes = {
  notification: PropTypes.object.isRequired,
  onRead: PropTypes.func,
};
